import type { CareerExplorerDataset } from '../types';
import dataAnalystFixture from './__fixtures__/data-analyst.json';
import registeredNurseFixture from './__fixtures__/registered-nurse.json';
import softwareDeveloperFixture from './__fixtures__/software-developer.json';
import projectManagerFixture from './__fixtures__/project-manager.json';

/**
 * Kept out of each fixture and merged in here: JSON imports widen
 * literal strings (like `'relevance'`) to `string`, which would
 * otherwise need a type assertion that hides a fixture missing a
 * required field.
 */
const DEFAULT_SORT: CareerExplorerDataset['sort'] = {
  default: 'relevance',
  rerankOnly: true,
};

type CareerFixtureData = Omit<CareerExplorerDataset, 'sort'>;

/**
 * A `Map` has no prototype chain to shadow a lookup, unlike a plain
 * object, where a career id like `'constructor'` or `'toString'` would
 * resolve to an inherited value instead of `undefined`.
 */
const fixtureData = new Map<string, CareerFixtureData>([
  ['data-analyst', dataAnalystFixture],
  ['registered-nurse', registeredNurseFixture],
  ['software-developer', softwareDeveloperFixture],
  ['project-manager', projectManagerFixture],
]);

/**
 * Returns the dataset for a career id, or `null` if unknown or empty.
 * Never throws. Reads static fixtures. Stays this way in Phase 2:
 * EDUN-16297 binds `programs` through a Product API DataSource at the
 * DSL/component level, and keeps this fixture as the fallback when
 * that DataSource is not configured, rather than making this
 * function fetch live data itself.
 */
export function getCareerDataset(
  careerId: string,
): CareerExplorerDataset | null {
  const trimmedId = careerId.trim();

  if (!trimmedId) return null;

  const data = fixtureData.get(trimmedId);

  return data ? { ...data, sort: DEFAULT_SORT } : null;
}
